import { useQueryClient } from "@tanstack/react-query";
import { isArray } from "lodash";
import { memo, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { AsyncSelectValue } from "../../../../../../../components/AsyncSelect";
// Specialty filter removed from PE import — CustomSelect no longer needed
// import CustomSelect from "../../../../../../../components/CustomSelect";
import { FlexCenterWrapper } from "../../../../../../../components/DefaultNoDataWrapper";
import Flex from "../../../../../../../components/Flex";
import StyledFormModal from "../../../../../../../components/globalModal/StyledModal";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import { useDebounce } from "../../../../../../../hooks/use-debounce";
import useDisclosure, {
  DisclosureHost,
} from "../../../../../../../hooks/use-disclosure";
import { useQueryParams } from "../../../../../../../hooks/use-query-param";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import { useRosPeTemplateCreate } from "../../../../../../../queries/encounter/use-ros-pe-template-create";
import { useRosPeTemplateUpdate } from "../../../../../../../queries/encounter/use-ros-pe-template-update";
import { useRosTemplateById } from "../../../../../../../queries/template/new-flow/use-new-template-by-id-list";
import { useNewTemplateList } from "../../../../../../../queries/template/new-flow/use-new-template-list";
// Specialty filter removed from PE import
// import { useSpecialtiesList } from "../../../../../../../queries/use-specialities-list";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../../../utils/utils";
import { PeAddTemplateDrawer } from "../../../../../admin/library/templates/RosTemplateDrawer";
import TemplateSelectRow from "./components/TemplateSelectRow";
import RosPeImportList from "./components/RosPeImportList";
import {
  HOME_CARE_PE_TEMPLATE_TYPE,
  PE_IMPORT_TEMPLATE_TYPES,
} from "./home-care/home-care-constant";
import { isHomeCareItem } from "./home-care/home-care-utils";
import { useImportItems } from "./hooks/use-import-items";

interface RosImportDrawerProps {
  disclosureHost: DisclosureHost;
}

const PeImportDrawer = memo(({ disclosureHost }: RosImportDrawerProps) => {
  const { mutateAsync: createPeTemplate } = useRosPeTemplateCreate();
  const { mutateAsync: updatePeTemplate } = useRosPeTemplateUpdate();
  const appointmentId = useQueryParams("appointmentid");
  const { patientId: routePatientId } = useParams();
  const queryPatientId = useQueryParams("patientid");
  const patientId = queryPatientId || routePatientId;

  const queryClient = useQueryClient();

  const peTemplateDisc = useDisclosure();
  const {
    items: pe,
    setItems: setPe,
    handleSelect,
    onDescriptionChange,
  } = useImportItems();
  const showSnackbar = useSnackbar();
  const [selectedTemplate, setSelectedTemplate] =
    useState<AsyncSelectValue | null>();
  // Specialty filter removed from PE import
  // const [speciality, setSpeciality] = useState<string | null>();
  const [searchKey, setSearchKey] = useDebounce<string>("");
  const [templatePagination, setTemplatePagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  // const [specialitySearch, setSpecialitySearch] = useDebounce<string>("");

  // const { data: specialities } = useSpecialtiesList({
  //   searchString: specialitySearch,
  //   xTENANTID: getXtenantId(),
  // });

  // const specialtiesList = useMemo(
  //   () =>
  //     specialities?.data?.content?.map((it) => ({
  //       value: it?.uuid,
  //       label: it?.name,
  //     })),
  //   [specialities]
  // );

  const { data, isLoading, isRefetching } = useNewTemplateList({
    enabled: disclosureHost.opened,
    pagination: templatePagination,
    gridSorting: [],
    searchKey,
    templateType: PE_IMPORT_TEMPLATE_TYPES,
    // specialityUuids: speciality ? [speciality] : undefined,
    xTENANTID: getXtenantId(),
  });

  const { data: templateByIdData } = useRosTemplateById({
    rosTemplateUuid: selectedTemplate?.value,
    xTENANTID: getXtenantId(),
  });

  const isHomeCareTemplate =
    !!pe?.length &&
    ((!!selectedTemplate?.value &&
      data?.data?.content?.find((it) => it.uuid === selectedTemplate.value)
        ?.templateTypes === HOME_CARE_PE_TEMPLATE_TYPE) ||
      (!selectedTemplate?.value && pe.every(isHomeCareItem)));

  useEffect(() => {
    if (selectedTemplate?.value) {
      setPe(
        templateByIdData?.data?.rosTemplateTypeSet?.map((it: any) => ({
          templateType: "PHYSICAL_EXAM",
          templateNameType: it?.name,
          description: it?.value,
          isSelected: it?.isSelected,
        })) ?? null
      );
    } else if (disclosureHost?.data && isArray(disclosureHost.data)) {
      setPe(disclosureHost?.data?.map((it) => ({ ...it, isSelected: true })));
    } else if (disclosureHost?.data) {
      setPe([{ ...disclosureHost?.data, isSelected: true }]);
    }
  }, [disclosureHost.opened, templateByIdData]);

  const handleSubmit = () => {
    const payload = {
      physicalROSTemplateRequest: {
        appointmentId: appointmentId ?? undefined,
        patientId: patientId ?? undefined,
        physicalROSTemplateList: pe?.filter((it) => !!it.isSelected),
      },
    };

    (disclosureHost?.data || !isArray(disclosureHost?.data)
      ? updatePeTemplate(payload)
      : createPeTemplate(payload)
    )
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));
        queryClient.invalidateQueries({
          queryKey: ["patient-ros-pe-template-list"],
        });

        closeAndReset();
      })
      .catch((err) => {
        showSnackbar(getErrorSnackbar(err));
      });
  };

  const closeAndReset = () => {
    setPe(null);
    setSelectedTemplate(null);
    // setSpeciality(null);
    disclosureHost.close();
  };

  const rosTemplateList = useMemo(() => {
    return data?.data?.content?.map((it) => ({
      value: it.uuid,
      label: it?.templateName,
    }));
  }, [data]);

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={closeAndReset}
      dialogTitle="Add PE"
      dialogAction={
        <Flex gap={1}>
          <StyledButtonV2
            label="Save"
            variant="contained"
            onClick={handleSubmit}
          />
          <StyledButtonV2
            label="Cancel"
            variant="outlined"
            onClick={closeAndReset}
          />
        </Flex>
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "50vw",
          height: "65vh",
        },
      }}
    >
      <Flex flexDirection={"column"} gap={1} width={"100%"} pt={2}>
        {(isArray(disclosureHost.data) || !disclosureHost?.data) && (
          <>
            <TemplateSelectRow
              isLoading={isLoading || isRefetching}
              value={selectedTemplate}
              options={rosTemplateList}
              onSearch={(v) => {
                setSearchKey(v);
                setTemplatePagination((p) => ({ ...p, page: 0 }));
              }}
              onChange={setSelectedTemplate}
              onAdd={() => peTemplateDisc.open()}
              pagination={templatePagination}
              setPagination={setTemplatePagination}
              totalElements={data?.data?.page?.totalElements}
            />
            <PeAddTemplateDrawer disclosureHost={peTemplateDisc} />
          </>
        )}
        {((!pe?.length && !disclosureHost?.data) ||
          (isArray(disclosureHost?.data) &&
            !disclosureHost?.data?.length &&
            !pe?.length)) && (
          <Flex height={"60vh"}>
            <FlexCenterWrapper>{"Please Select Template"}</FlexCenterWrapper>
          </Flex>
        )}
        {pe && (
          <RosPeImportList
            items={pe}
            isHomeCare={isHomeCareTemplate}
            onSelect={handleSelect}
            onDescriptionChange={onDescriptionChange}
          />
        )}
      </Flex>
    </StyledFormModal>
  );
});

export default PeImportDrawer;
